import { useEffect, useState } from 'react';
import { doMeiliSearch } from '../../music24/components/SearchField/searchService';
import { MeiliIndex, MeiliTrackData } from '../../music24/components/SearchField/types';

/** Case- and space-insensitive, the way the catalog compares two titles. */
function same(left: string | undefined, right: string | undefined): boolean {
  if (left === undefined || right === undefined) {
    return false;
  }
  return left.trim().toLowerCase() === right.trim().toLowerCase();
}

/**
 * The catalog track behind a release the artist is adding by hand, if there is
 * one after all.
 *
 * A release reaches this sheet because a catalog search did not find it, but
 * the artist searched by the name they use and we search by what the service
 * says the release is called. Those differ often enough to be worth one more
 * look. A hit only counts when the title and the artist both match exactly —
 * a near match here would file the artist's release under somebody else's
 * track, and every page built on it afterwards would read the wrong numbers.
 *
 * `undefined` is the normal answer, and it costs nothing: the row is then
 * stored under a key of our own.
 */
export function useCatalogMaidForRelease(
  title: string | undefined,
  artistName: string | undefined
): string | undefined {
  const [catalogTrackMaid, setCatalogTrackMaid] = useState<string>();

  useEffect(() => {
    if (title === undefined || title.trim() === '' || artistName === undefined || artistName.trim() === '') {
      setCatalogTrackMaid(undefined);
      return undefined;
    }
    let isCancelled = false;

    doMeiliSearch(`${title} ${artistName}`, [MeiliIndex.tracks])
      .then((response) => {
        if (isCancelled) {
          return;
        }
        const hit = (response?.[0]?.items ?? [])
          .filter((item) => item.type === MeiliIndex.tracks)
          .map((item) => item.item as MeiliTrackData)
          .find((track) => same(track.name, title) && same(track.artistName, artistName));
        setCatalogTrackMaid(hit?.id);
      })
      .catch(() => {
        if (!isCancelled) {
          setCatalogTrackMaid(undefined);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [artistName, title]);

  return catalogTrackMaid;
}
